import React, { useState } from 'react';
import { Loader2, Sparkles, Volume2 } from 'lucide-react';
import type { ListingData, PropertyCutAsset } from '../../types';
import {
  VOICEOVER_VOICES,
  draftVoiceoverScript,
  listingVoiceoverContext,
  speakVoiceover,
  type VoiceoverLength,
  type VoiceoverTone,
} from '../../services/voiceover';

const HIGHLIGHTS = [
  'spacious',
  'recently renovated',
  'open-plan living',
  'modern kitchen',
  'period features',
  'south-facing garden',
  'off-street parking',
  'close to transport',
  'chain-free',
  'views',
];

const TONES: { id: VoiceoverTone; label: string }[] = [
  { id: 'warm', label: 'Warm' },
  { id: 'professional', label: 'Professional' },
  { id: 'luxury', label: 'Luxury' },
  { id: 'friendly', label: 'Friendly' },
];

const LENGTHS: { id: VoiceoverLength; label: string }[] = [
  { id: 'short', label: '15s' },
  { id: 'medium', label: '30s' },
  { id: 'long', label: '60s' },
];

export default function VoiceoverComposer(props: {
  projectId: string;
  listing: ListingData;
  disabled?: boolean;
  onReady: (asset: PropertyCutAsset) => void;
}) {
  const [script, setScript] = useState('');
  const [tone, setTone] = useState<VoiceoverTone>('warm');
  const [length, setLength] = useState<VoiceoverLength>('medium');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [voiceId, setVoiceId] = useState<string>(VOICEOVER_VOICES[0].id);
  const [drafting, setDrafting] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDraft = selected.length > 0 || notes.trim().length > 0 || listingVoiceoverContext(props.listing).length > 0;
  const busy = drafting || speaking || props.disabled;

  const draft = async () => {
    if (!canDraft || busy) return;
    setDrafting(true);
    setError(null);
    try {
      const text = await draftVoiceoverScript({
        listing: props.listing,
        features: selected,
        tone,
        length,
        notes,
      });
      setScript(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not draft the script.');
    } finally {
      setDrafting(false);
    }
  };

  const speak = async () => {
    const text = script.trim();
    if (!text || busy) return;
    setSpeaking(true);
    setError(null);
    try {
      const voice = VOICEOVER_VOICES.find((item) => item.id === voiceId) ?? VOICEOVER_VOICES[0];
      const asset = await speakVoiceover({
        projectId: props.projectId,
        text,
        voiceId: voice.id,
        voiceLabel: voice.label.split('—')[0].trim(),
      });
      props.onReady(asset);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the voiceover.');
    } finally {
      setSpeaking(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium text-white">
        <Sparkles className="h-3.5 w-3.5 text-amber-300" />
        Write a voiceover
      </div>
      <textarea
        value={script}
        onChange={(event) => setScript(event.target.value.slice(0, 5000))}
        placeholder="Draft a script from the listing, or type one yourself."
        rows={5}
        disabled={busy}
        className="w-full resize-none rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-2 text-xs text-white placeholder:text-slate-600 focus:border-teal-400 focus:outline-none"
      />
      <div className="flex gap-1">
        {TONES.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={busy}
            onClick={() => setTone(item.id)}
            className={`rounded-md px-2 py-1 text-[10px] ${tone === item.id ? 'bg-teal-400 text-slate-950' : 'bg-slate-800 text-slate-300'}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        {LENGTHS.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={busy}
            onClick={() => setLength(item.id)}
            className={`rounded-md px-2 py-1 text-[10px] ${length === item.id ? 'bg-slate-200 text-slate-950' : 'bg-slate-800 text-slate-300'}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {HIGHLIGHTS.map((feature) => {
          const on = selected.includes(feature);
          return (
            <button
              key={feature}
              type="button"
              disabled={busy}
              onClick={() => setSelected((current) => (
                on ? current.filter((item) => item !== feature) : [...current, feature]
              ))}
              className={`rounded-full px-2 py-0.5 text-[10px] ${on ? 'bg-teal-400/20 text-teal-200 ring-1 ring-teal-400/50' : 'bg-slate-800 text-slate-400'}`}
            >
              {feature}
            </button>
          );
        })}
      </div>
      <input
        value={notes}
        onChange={(event) => setNotes(event.target.value.slice(0, 500))}
        placeholder="Anything else to mention"
        disabled={busy}
        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-xs text-white placeholder:text-slate-600 focus:border-teal-400 focus:outline-none"
      />
      <button
        type="button"
        onClick={() => void draft()}
        disabled={busy || !canDraft}
        className="w-full rounded-lg border border-slate-600 py-2 text-xs text-slate-200 disabled:opacity-50"
      >
        {drafting ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : 'Draft script'}
      </button>
      <select
        value={voiceId}
        onChange={(event) => setVoiceId(event.target.value)}
        disabled={busy}
        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-white focus:border-teal-400 focus:outline-none"
      >
        {VOICEOVER_VOICES.map((voice) => (
          <option key={voice.id} value={voice.id}>{voice.label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => void speak()}
        disabled={busy || !script.trim()}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-teal-400 py-2 text-xs font-semibold text-slate-950 disabled:bg-slate-800 disabled:text-slate-500"
      >
        {speaking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}
        {speaking ? 'Speaking…' : 'Speak with ElevenLabs · 1 credit'}
      </button>
      <p className="text-[10px] leading-snug text-slate-500">Drafting is free. The voice uses the same ElevenLabs voices as 360 Tours.</p>
      {error && <p className="text-[11px] text-amber-200">{error}</p>}
    </div>
  );
}
